import posthog from "posthog-js";
import { api } from "@/lib/api/client";
import { notify } from "@/providers/sonner";
import type { LoanApplicationData, useNbiForm } from "../hooks/useNbiForm";
import type { DocumentValidationIssue } from "../types/documentTypes";
import { parseDocumentEvent } from "./documentParser";
import { isDocumentUploadTimedOut } from "./documentTimeout";

export { DOCUMENT_UPLOAD_TIMEOUT_MS, isDocumentUploadTimedOut } from "./documentTimeout";

interface UploadState {
    status: string;
    progress: number;
    fileName: string;
    documentId?: number;
    file?: File;
    size?: number;
    error?: string;
    uploadedAt?: number;
}

interface DocumentInfo {
    id: number;
    fileName: string;
    status: string;
    size?: number;
    documentValidation?: DocumentValidationIssue[];
    processingError?: Record<string, unknown> | null;
}

const readFileAsBase64 = (file: File): Promise<{ base64: string; mimeType: string; size: number }> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            resolve({ base64, mimeType: file.type, size: file.size });
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
    });

export const processDocument = async (
    doc: DocumentInfo,
    uploadState: UploadState,
    applicationId: number,
    form: ReturnType<typeof useNbiForm>,
    onComplete: () => void,
    defaultCurrency: string = "AUD"
): Promise<void> => {
    const setField = (field: string, value: unknown) => form.setFieldValue(field as any, value);

    try {
        const documentProcessData = await api.getDocumentProcessData(applicationId, doc.id);
        parseDocumentEvent(
            documentProcessData,
            form.state.values as LoanApplicationData,
            setField,
            defaultCurrency
        );
    } catch (error) {
        posthog.captureException(error instanceof Error ? error : new Error(String(error)), {
            document_id: doc.id,
            file_name: doc.fileName,
            source: "frontend",
            context: "parseDocumentEvent",
        });
    }

    let contentBase64: string;
    let mimeType: string;
    let size: number;

    if (uploadState.file) {
        try {
            ({ base64: contentBase64, mimeType, size } = await readFileAsBase64(uploadState.file));
        } catch {
            notify.error({ title: "Failed to read file.", description: doc.fileName });
            throw new Error("Failed to read file");
        }
    } else {
        ({ contentBase64, mimeType } = await api.getDocumentBytes(applicationId, doc.id));
        size = doc.size ?? uploadState.size ?? 0;
    }

    const newDoc = {
        id: doc.id,
        fileName: doc.fileName,
        status: "success" as const,
        uploadedAt: new Date().toISOString(),
        mimeType,
        size,
        contentBase64,
        documentValidation: doc.documentValidation || [],
    };

    const currentValues = form.state.values as LoanApplicationData;
    form.setFieldValue("documents" as any, [...(currentValues.documents || []), newDoc]);
    form.setFieldValue(
        "uploadStates" as any,
        (currentValues.uploadStates || []).filter((s: UploadState) => s.documentId !== doc.id)
    );
    onComplete();
    notify.info({ title: "Processed.", description: doc.fileName });

    // Cross-doc validation runs after the commit so a failure here cannot block
    // document processing or leave the document stuck in "processing" state.
    // Failures are reported to posthog rather than propagated.
    try {
        await api.validateApplicationFields(applicationId);
        const docIdsToRefetch = new Set([
            ...((form.state.values as LoanApplicationData).documents || [])
                .filter((d: any) => d.status === "success")
                .map((d: any) => d.id as number),
            doc.id,
        ]);
        await Promise.all(
            Array.from(docIdsToRefetch).map(async (id) => {
                const data = await api.getDocumentProcessData(applicationId, id);
                parseDocumentEvent(data, form.state.values as LoanApplicationData, setField, defaultCurrency);
            })
        );
    } catch (error) {
        posthog.captureException(error instanceof Error ? error : new Error(String(error)), {
            document_id: doc.id,
            source: "frontend",
            context: "refreshCrossDocValidation",
        });
    }
};

export const handleDocumentError = (
    doc: DocumentInfo,
    form: ReturnType<typeof useNbiForm>,
    onComplete: () => void
): void => {
    if (doc.processingError) {
        console.error("[DocumentUpload] Background processing failed", {
            documentId: doc.id,
            fileName: doc.fileName,
            processingError: doc.processingError,
        });
    }

    const currentStates = (form.state.values as LoanApplicationData).uploadStates || [];
    const updatedStates = currentStates.map((s: UploadState) =>
        s.documentId === doc.id
            ? {
                  ...s,
                  status: "error" as const,
                  error: "Processing failed",
                  progress: 0,
              }
            : s
    );

    form.setFieldValue("uploadStates" as any, updatedStates);
    onComplete();

    notify.error({
        title: "Failed.",
        description: doc.fileName,
    });
};

export const handleDocumentTimeout = (
    doc: DocumentInfo,
    form: ReturnType<typeof useNbiForm>,
    onComplete: () => void
): void => {
    const currentStates = (form.state.values as LoanApplicationData).uploadStates || [];
    const updatedStates = currentStates.map((s: UploadState) =>
        s.documentId === doc.id
            ? {
                  ...s,
                  status: "error" as const,
                  error: "Timeout",
                  progress: 0,
              }
            : s
    );

    form.setFieldValue("uploadStates" as any, updatedStates);
    onComplete();

    notify.error({
        title: "Timeout.",
        description: doc.fileName,
    });
};

export const handleDocumentPendingReview = (
    doc: DocumentInfo,
    uploadState: UploadState,
    form: ReturnType<typeof useNbiForm>,
    onComplete: () => void
): void => {
    if (!uploadState.file) return;

    const reader = new FileReader();

    reader.onload = () => {
        const base64 = (reader.result as string).split(",")[1];

        const newDoc = {
            id: doc.id,
            fileName: doc.fileName,
            status: "pending_review" as const,
            uploadedAt: new Date().toISOString(),
            mimeType: uploadState.file!.type,
            size: uploadState.file!.size,
            contentBase64: base64,
            documentValidation: [],
        };

        const currentValues = form.state.values as LoanApplicationData;
        const updatedUploadStates = (currentValues.uploadStates || []).filter(
            (s: UploadState) => s.documentId !== doc.id
        );

        form.setFieldValue("documents" as any, [...(currentValues.documents || []), newDoc]);
        form.setFieldValue("uploadStates" as any, updatedUploadStates);
        onComplete();
    };

    reader.onerror = () => {
        handleDocumentError(doc, form, onComplete);
    };

    reader.readAsDataURL(uploadState.file);
};

export const checkDocumentTimeout = (uploadState: UploadState): boolean => {
    return isDocumentUploadTimedOut(uploadState.uploadedAt, Date.now());
};
